import type { DocumentData } from 'firebase/firestore'
import { collection, deleteDoc, doc, getDoc, getDocs, query, serverTimestamp, where, writeBatch } from 'firebase/firestore'
import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  updateProfile,
} from 'firebase/auth'
import { accountant, deliver, getBillers as getBiller, getdeliver, manager, owner } from '../firestoreWrappers/firestoreWrappers'
import { errorDefault, errorEmail, savedDefault } from '../helpers/snackbar'
import { isBiller, isGetDeliver, ROLE } from '../helpers/roles'
import { auth, firestore, secondaryFirebaseApp } from '../firebaseCore'
import { generateRandomText } from '../helpers/generateRandomText'
import { getIdFromRef } from '../helpers/getIdFromRef'
import {
  mapDeliver,
  mapOwners,
} from '../models/ManagerModel'
import { CompanyModel } from '~/models/CompanyModel'
import type { ManagerModel, OwnerModel } from '~/models/OwnerModel'
import { mapManagers } from '~/models/OwnerModel'
import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { ref } from 'vue'
import type { DeliverModel } from '~/models/DeliverModel'
import type { EmployeeModel } from '~/models/EmployeeModel'
import { useSuppliersStore } from './suppliers'
import { useChangeLogsStore } from './changeLogsStore'
import { useSnackbarStore } from './snackbar'

// @ts-expect-error
function sortByCities(query1, { 'profile': { cities } }) {
  return query(
    query1,
    where(
      'profile.cities',
      'array-contains-any',
      cities,
    ),
  )
}

export const useEmployeesStore = defineStore('employees', () => {
  const delivers: Ref<DeliverModel[] | null> = ref(null)
  const managers: Ref<ManagerModel[] | null> = ref(null)
  const accountants: Ref<ManagerModel[] | null> = ref(null)
  const owners: Ref<OwnerModel[] | null> = ref(null)
  const getdelivers: Ref<DeliverModel[] | null> = ref(null)
  const billers: Ref<any[] | null> = ref(null)
  const resetPasswordLoading: Ref<boolean> = ref(false)
  const resetPasswordError: Ref<unknown | null> = ref(null)
  const fetchLoading: Ref<boolean> = ref(false)
  const fetchError: Ref<unknown | null> = ref(null)
  const fetchDeliversLoading: Ref<boolean> = ref(false)
  const fetchDeliversError: Ref<unknown | null> = ref(null)
  const fetchGetDeliversLoading: Ref<boolean> = ref(false)
  const fetchGetDeliversError: Ref<unknown | null> = ref(null)
  const createLoading: Ref<boolean> = ref(false)
  const createError: Ref<unknown | null> = ref(null)

  const suppliersStore = useSuppliersStore()
  const changeLogsStore = useChangeLogsStore()

  const resetState = () => {
    delivers.value = null
    managers.value = null
    accountants.value = null
    owners.value = null
    getdelivers.value = null
    billers.value = null
    resetPasswordLoading.value = false
    resetPasswordError.value = null
    fetchLoading.value = false
    fetchError.value = null
    fetchDeliversLoading.value = false
    fetchDeliversError.value = null
    fetchGetDeliversLoading.value = false
    fetchGetDeliversError.value = null
    createLoading.value = false
    createError.value = null
  }

  function getGetDelivers(userData: any) {
    if (getdelivers.value)
      return

    fetchError.value = null
    fetchGetDeliversLoading.value = true
    fetchLoading.value = true

    const deliversLocal: DeliverModel[] = [...(getdelivers.value || [])]

    let deliversQuery: any = getdeliver(userData.company)
    if (userData.role == ROLE.GET_DELIVER || userData.role == ROLE.MANAGER) {
      deliversQuery = sortByCities(
        deliversQuery,
        userData,
      )
    }

    const onSuccess = (deliversDataList: DocumentData) => {
      deliversLocal.push(...deliversDataList.docs.map(mapDeliver))
      let data = deliversLocal
      if (isGetDeliver(userData.role)) {
        const myObject = deliversLocal.find(item => item.reference?.id === userData.reference.id)
        data = data.filter(item => (item.visibilityAccess || []).map((item1: any) => item1.id).includes(userData.reference.id))
        data = [
          // @ts-expect-error
          ...data,
          // @ts-expect-error
          myObject,
        ]
      }
      if (isBiller(userData.role))
        data = data.filter(item => (item.visibilityAccess || []).map((item1: any) => item1.id).includes(userData.reference.id))

      fetchLoading.value = false
      fetchGetDeliversLoading.value = false
      fetchError.value = null
      getdelivers.value = data
    }

    const onError = (error: unknown) => {
      fetchLoading.value = false
      fetchGetDeliversLoading.value = false
      fetchError.value = error
    }

    getDocs(deliversQuery).then(onSuccess).catch(onError)
  }

  function getGetDelivers2(userData: any) {
    if (getdelivers.value)
      return

    fetchError.value = null
    fetchGetDeliversLoading.value = true
    fetchLoading.value = true

    const deliversLocal: DeliverModel[] = [...(getdelivers.value || [])]

    let deliversQuery: any = getdeliver(userData.company)
    if (userData.role == ROLE.GET_DELIVER || userData.role == ROLE.MANAGER) {
      deliversQuery = sortByCities(
        deliversQuery,
        userData,
      )
    }

    const onSuccess = (deliversDataList: any) => {
      if (isBiller(userData.role))
        deliversLocal.push(...deliversDataList.docs.map(mapDeliver).filter((item: DeliverModel) => (item.visibilityAccess || []).map(item1 => item1.id).includes(userData.reference.id)))
      else
        deliversLocal.push(...deliversDataList.docs.map(mapDeliver))

      fetchLoading.value = false
      fetchGetDeliversLoading.value = false
      fetchError.value = null
      getdelivers.value = deliversLocal
    }

    const onError = (error: unknown) => {
      console.error(error)
      fetchLoading.value = false
      fetchGetDeliversLoading.value = false
      fetchError.value = error
    }

    getDocs(deliversQuery).then(onSuccess).catch(onError)
  }

  function getDelivers(userData: any) {
    if (delivers.value)
      return

    fetchError.value = null
    fetchDeliversLoading.value = true
    fetchLoading.value = true

    let deliversQuery: any = deliver(userData.company)
    if (userData.role == ROLE.GET_DELIVER) {
      deliversQuery = sortByCities(
        deliversQuery,
        userData,
      )
    }

    getDocs(deliversQuery)
      .then((deliversDataList) => {
        const deliversLocal = [...(delivers.value || [])]
        deliversLocal.push(...deliversDataList.docs.map(mapDeliver))

        let data = deliversLocal
        if (isGetDeliver(userData.role) || isBiller(userData.role))

          data = data.filter(item => (item.visibilityAccess || []).map(item1 => item1.id).includes(userData.reference.id))

        fetchLoading.value = false
        fetchDeliversLoading.value = false
        fetchError.value = null
        delivers.value = data
      })
      .catch((error) => {
        console.error(error)
        fetchLoading.value = false
        fetchDeliversLoading.value = false
        fetchError.value = error
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      })
  }

  function getManagers(userData: any) {
    fetchError.value = null
    fetchLoading.value = true

    const onSuccess = (managersList: { docs: DocumentData[] }) => {
      fetchLoading.value = false
      fetchError.value = null
      managers.value = managersList.docs.map(mapManagers)
    }

    const onError = (error: unknown) => {
      console.error(error)
      fetchLoading.value = false
      fetchError.value = error

      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }

    let managersQuery: any = manager(userData.company)

    if (userData.role == ROLE.GET_DELIVER || userData.role == ROLE.MANAGER) {
      managersQuery = sortByCities(
        managersQuery,
        userData,
      )
    }

    getDocs(managersQuery).then(onSuccess).catch(onError)
  }

  function getBillers(userData: any) {
    fetchError.value = null
    fetchLoading.value = true

    const onSuccess = (billersList: DocumentData) => {
      fetchLoading.value = false
      fetchError.value = null
      billers.value = billersList.docs.map(mapDeliver)
    }

    const onError = (error: unknown) => {
      console.error(error)
      fetchLoading.value = false
      fetchError.value = error

      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }

    let billersQuery: any = getBiller(userData.company)

    if (userData.role == ROLE.BILLER) {
      billersQuery = sortByCities(
        billersQuery,
        userData,
      )
    }

    getDocs(billersQuery).then(onSuccess).catch(onError)
  }

  function getAccountants(userData: any) {
    fetchError.value = null
    fetchLoading.value = true

    const onSuccess = (accountantsList: DocumentData) => {
      fetchLoading.value = false
      fetchError.value = null
      accountants.value = accountantsList.docs.map(mapManagers)
    }

    const onError = (error: unknown) => {
      console.error(error)
      fetchLoading.value = false
      fetchError.value = error

      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }

    getDocs(accountant(userData.company)).then(onSuccess).catch(onError)
  }

  function getOwners(userData: any) {
    fetchError.value = null
    fetchLoading.value = true

    const onSuccess = (ownersList: DocumentData) => {
      fetchLoading.value = false
      fetchError.value = null
      owners.value = ownersList.docs.map(mapOwners).filter((item: any) => !item.hidden)
    }

    const onError = (error: unknown) => {
      console.error(error)
      fetchLoading.value = false
      fetchError.value = error

      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }

    getDocs(owner(userData.company))
      .then(onSuccess)
      .catch(onError)
  }

  async function editEmployee(employee: any, oldRole: string | null, userData: any) {
    createLoading.value = true
    createError.value = null

    const getCorrectData = (role: string) => {
      if (role === ROLE.GET_DELIVER)
        return getdelivers.value
      if (role === ROLE.DELIVER)
        return delivers.value
      if (role === ROLE.MANAGER)
        return managers.value
      if (role === ROLE.BILLER)
        return billers.value
      if (role === ROLE.OWNER)
        return owners.value
      if (role === ROLE.ACCOUNTANT)
        return accountants.value
      return []
    }

    const newEmployees = [...(getCorrectData(employee.role || '') || [])]
    const oldEmployees = [...(getCorrectData(oldRole || '') || [])]

    const byRef = (user: EmployeeModel) => getIdFromRef(user.reference?.path) == getIdFromRef(employee.reference?.path)
    const employeeIndex = newEmployees.findIndex(byRef)
    const oldEmployee = { ...oldEmployees.find(byRef) || {} }

    try {
      const batch = writeBatch(firestore)

      batch.update(
        employee.reference,
        {
          ...employee.toMap(),
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )

      if (employee?.isOwner || employee?.isOwner === false) {
        const response = await getDoc(userData.company)
        const company = new CompanyModel(
          // @ts-expect-error
          response.data(),
          response.ref,
        )
        if (
          (!company?.agreement
            || !Object.keys(company?.agreement.acceptedBy || {}).includes(getIdFromRef(employee.reference)))
          && company.reference
        ) {
          batch.update(
            company.reference,
            {
              agreement: {
                ...(company?.agreement || {}),
                acceptedBy: {
                  ...(company?.agreement?.acceptedBy || {}),
                  [getIdFromRef(employee.reference)]: false,
                },
              },
              lastUpdateByUser: userData.reference,
              lastUpdateTime: serverTimestamp(),
            },
          )
        }
        else if (employee?.isOwner === false && company?.reference) {
          const agreementCopy = company?.agreement?.acceptedBy
          // @ts-expect-error
          delete agreementCopy[getIdFromRef(employee.reference)]
          batch.update(
            company.reference,
            {
              agreement: {
                ...(company?.agreement || {}),
                acceptedBy: agreementCopy,
              },
              lastUpdateByUser: userData.reference,
              lastUpdateTime: serverTimestamp(),
            },
          )
        }
      }
      await batch.commit()

      if (employeeIndex === -1)
        newEmployees.push(employee)
      else
        newEmployees[employeeIndex] = employee

      const byRef1 = (user: EmployeeModel) => getIdFromRef(user.reference) !== getIdFromRef(employee.reference)

      createLoading.value = false

      if (employee.role === ROLE.GET_DELIVER)
        getdelivers.value = newEmployees
      if (employee.role === ROLE.DELIVER)
        delivers.value = newEmployees
      if (employee.role === ROLE.MANAGER)
        managers.value = newEmployees
      if (employee.role === ROLE.BILLER)
        billers.value = newEmployees
      if (employee.role === ROLE.OWNER)
        owners.value = newEmployees
      if (employee.role === ROLE.ACCOUNTANT)
        accountants.value = newEmployees

      createError.value = null
      if (oldRole) {
        if (oldRole === ROLE.GET_DELIVER)
          getdelivers.value = [...(getdelivers.value || [])].filter(byRef1)
        if (oldRole === ROLE.DELIVER)
          delivers.value = [...(delivers.value || [])].filter(byRef1)
        if (oldRole === ROLE.MANAGER)
          managers.value = [...(managers.value || [])].filter(byRef1)
        if (oldRole === ROLE.BILLER)
          billers.value = [...(billers.value || [])].filter(byRef1)
        if (oldRole === ROLE.OWNER)
          owners.value = [...(owners.value || [])].filter(byRef1)
        if (oldRole === ROLE.ACCOUNTANT)
          accountants.value = [...(accountants.value || [])].filter(byRef1)
      }

      changeLogsStore.addLog(
        oldEmployee,
        employee,
        userData.reference,
        `${userData.profile.firstName} ${userData.profile.lastName}`,
        `${employee.profile.firstName} ${employee.profile.lastName}`,
        oldEmployee.reference,
        'other',
        null,
        null,
        userData.company,
      )

      useSnackbarStore().showSnackbar(savedDefault,
      )
      if (employee.role === ROLE.DELIVER) {
        const isDeliver = (user: EmployeeModel) => user.role === ROLE.DELIVER
        const deliversTmp = newEmployees.filter(isDeliver)
        suppliersStore.setDelivers(deliversTmp)
      }
    }
    catch (error: unknown) {
      console.error(error)
      createLoading.value = false
      createError.value = error

      useSnackbarStore().showSnackbar(errorEmail(error),
      )
    }
  }

  function resetEmployeePassword(email: string) {
    resetPasswordLoading.value = true
    resetPasswordError.value = null

    const onSuccess = () => {
      resetPasswordLoading.value = false
      resetPasswordError.value = null

      useSnackbarStore().showSnackbar(savedDefault,
      )
    }
    const onError = (error: unknown) => {
      console.error(error)
      resetPasswordLoading.value = false
      resetPasswordError.value = error

      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }
    sendPasswordResetEmail(
      auth,
      email,
    )
      .then(onSuccess)
      .catch(onError)
  }

  async function createEmployee(employee: any, userData: any) {
    createLoading.value = true
    createError.value = null

    const getCorrectData = (role: string) => {
      if (role === ROLE.GET_DELIVER)
        return getdelivers.value
      if (role === ROLE.DELIVER)
        return delivers.value
      if (role === ROLE.MANAGER)
        return managers.value
      if (role === ROLE.BILLER)
        return billers.value
      if (role === ROLE.OWNER)
        return owners.value
      if (role === ROLE.ACCOUNTANT)
        return accountants.value
      return []
    }

    const employeesState = getCorrectData(employee.role)
    const newEmployees = employeesState?.length
      ? [...employeesState]
      : []
    let newEmployeeRef
    const batch = writeBatch(firestore)
    const password = generateRandomText()
    const userRole = (employee.role === ROLE.GET_DELIVER || employee.role === ROLE.BILLER)
      ? ROLE.DELIVER
      : (employee.role === ROLE.MANAGER || employee.role === ROLE.ACCOUNTANT)
          ? ROLE.MANAGER
          : employee.role
    let auth2

    try {
      auth2 = getAuth(secondaryFirebaseApp)

      await createUserWithEmailAndPassword(
        auth2,
        employee.profile.email,
        password,
      )
      await updateProfile(
        // @ts-expect-error
        auth2.currentUser,
        { displayName: userRole },
      )
      // @ts-expect-error
      await sendEmailVerification(auth2.currentUser)
      await sendPasswordResetEmail(
        auth2,
        employee.profile.email,
      )
      newEmployeeRef = doc(
        collection(
          firestore,
          `${userRole}s`,
        ),
        // @ts-expect-error
        auth2.currentUser.uid,
      )
      batch.set(
        newEmployeeRef,
        {
          ...employee.toMap(),
          createdByUser: userData.reference,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
      employee.reference = newEmployeeRef

      await batch.commit()
      newEmployees.push(employee)

      changeLogsStore.addNewDocumentLog(
        userData.reference,
        `${userData.profile.firstName} ${userData.profile.lastName}`,
        `${employee.profile.firstName} ${employee.profile.lastName}`,
        employee.reference,
        'other',
        null,
        null,
        userData.company,
      )

      createLoading.value = false

      if (employee.role === ROLE.GET_DELIVER)
        getdelivers.value = newEmployees
      if (employee.role === ROLE.DELIVER)
        delivers.value = newEmployees
      if (employee.role === ROLE.MANAGER)
        managers.value = newEmployees
      if (employee.role === ROLE.BILLER)
        billers.value = newEmployees
      if (employee.role === ROLE.OWNER)
        owners.value = newEmployees
      if (employee.role === ROLE.ACCOUNTANT)
        accountants.value = newEmployees

      createError.value = null

      useSnackbarStore().showSnackbar(savedDefault,
      )
      await signOut(auth2)
    }
    catch (error: unknown) {
      console.error('Error creating employee:', error)
      if (auth2?.currentUser)
        await deleteUser(auth2.currentUser)

      if (newEmployeeRef)
        await deleteDoc(newEmployeeRef)

      createLoading.value = false
      createError.value = error
      console.error(error)
      useSnackbarStore().showSnackbar(errorEmail(error),
      )
    }
  }

  return {
    delivers,
    managers,
    accountants,
    owners,
    getdelivers,
    billers,
    resetPasswordLoading,
    resetPasswordError,
    fetchLoading,
    fetchError,
    fetchDeliversLoading,
    fetchDeliversError,
    fetchGetDeliversLoading,
    fetchGetDeliversError,
    createLoading,
    createError,
    resetState,
    getGetDelivers,
    getGetDelivers2,
    getDelivers,
    getManagers,
    getBillers,
    getAccountants,
    getOwners,
    editEmployee,
    resetEmployeePassword,
    createEmployee,
  }
})
